import React, { useState, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { WorkflowCanvasMode } from '../types';

// ── Phase type metadata ───────────────────────────────────────────────────────

const PHASE_META: Record<string, {
  label: string; color: string; darkColor: string; icon: string;
  cardBg: string; cardDarkBg: string; cardBorder: string; cardDarkBorder: string;
}> = {
  team_selection: {
    label:          'Team Selection',
    color:          'bg-violet-100 text-violet-800 border-violet-300',
    darkColor:      'bg-violet-900/40 text-violet-300 border-violet-700',
    icon:           '👥',
    cardBg:         'bg-violet-50',
    cardDarkBg:     'bg-violet-950/60',
    cardBorder:     'border-violet-200',
    cardDarkBorder: 'border-violet-800',
  },
  brainstorming: {
    label:          'Brainstorming',
    color:          'bg-blue-100 text-blue-800 border-blue-300',
    darkColor:      'bg-blue-900/40 text-blue-300 border-blue-700',
    icon:           '💡',
    cardBg:         'bg-blue-50',
    cardDarkBg:     'bg-blue-950/60',
    cardBorder:     'border-blue-200',
    cardDarkBorder: 'border-blue-800',
  },
  critique: {
    label:          'Critique',
    color:          'bg-orange-100 text-orange-800 border-orange-300',
    darkColor:      'bg-orange-900/40 text-orange-300 border-orange-700',
    icon:           '🔍',
    cardBg:         'bg-orange-50',
    cardDarkBg:     'bg-orange-950/60',
    cardBorder:     'border-orange-200',
    cardDarkBorder: 'border-orange-800',
  },
  synthesis: {
    label:          'Synthesis',
    color:          'bg-green-100 text-green-800 border-green-300',
    darkColor:      'bg-green-900/40 text-green-300 border-green-700',
    icon:           '🔗',
    cardBg:         'bg-green-50',
    cardDarkBg:     'bg-green-950/60',
    cardBorder:     'border-green-200',
    cardDarkBorder: 'border-green-800',
  },
  refinement: {
    label:          'Refinement',
    color:          'bg-teal-100 text-teal-800 border-teal-300',
    darkColor:      'bg-teal-900/40 text-teal-300 border-teal-700',
    icon:           '✨',
    cardBg:         'bg-teal-50',
    cardDarkBg:     'bg-teal-950/60',
    cardBorder:     'border-teal-200',
    cardDarkBorder: 'border-teal-800',
  },
  default: {
    label:          'Phase',
    color:          'bg-gray-100 text-gray-800 border-gray-300',
    darkColor:      'bg-gray-800 text-gray-300 border-gray-600',
    icon:           '⚙️',
    cardBg:         'bg-gray-50',
    cardDarkBg:     'bg-gray-900/60',
    cardBorder:     'border-gray-200',
    cardDarkBorder: 'border-gray-700',
  },
};

const STATUS_META: Record<string, { label: string; color: string; dot: string }> = {
  not_started: { label: 'Not Started', color: 'text-gray-500',  dot: 'bg-gray-400' },
  in_progress:  { label: 'In Progress', color: 'text-blue-500',  dot: 'bg-blue-500 animate-pulse' },
  completed:    { label: 'Completed',   color: 'text-green-500', dot: 'bg-green-500' },
  failed:       { label: 'Failed',      color: 'text-red-500',   dot: 'bg-red-500' },
  skipped:      { label: 'Skipped',     color: 'text-gray-400',  dot: 'bg-gray-300' },
};

// ── Main component ────────────────────────────────────────────────────────────

const UnifiedPhaseNode = ({ data, isConnectable, selected = false }) => {
  const {
    mode,
    status,
    label,
    type,
    config,
    onCriticalPath,
    execution,
    darkMode,
    expanded,
    isValidConnectionTarget,
    isConnectionSource,
  } = data;

  const [isEditing,   setIsEditing]   = useState(false);
  const [isExpanded,  setIsExpanded]  = useState(true); // always open by default

  // Local edit state — mirrors config fields
  const [editLabel,           setEditLabel]           = useState(label || '');
  const [editType,            setEditType]            = useState(type || 'default');
  const [editMission,         setEditMission]         = useState(config?.mission || '');
  const [editObjectives,      setEditObjectives]      = useState<string>(
    (config?.objectives || []).join('\n')
  );
  const [editSuccessCriteria, setEditSuccessCriteria] = useState(config?.success_criteria || '');
  const [editNotes,           setEditNotes]           = useState(config?.notes || '');

  const canEdit = mode === WorkflowCanvasMode.EDIT;
  const meta    = PHASE_META[type] ?? PHASE_META.default;
  const statusM = STATUS_META[status ?? 'not_started'] ?? STATUS_META.not_started;

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const update = useCallback((updates: Record<string, any>) => {
    data.onUpdate?.(data.id, updates);
  }, [data]);

  const toggleExpand = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(v => {
      const next = !v;
      data.onToggleExpand?.(data.id, next);
      return next;
    });
  }, [data]);

  const openEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setEditLabel(label || '');
    setEditType(type || 'default');
    setEditMission(config?.mission || '');
    setEditObjectives((config?.objectives || []).join('\n'));
    setEditSuccessCriteria(config?.success_criteria || '');
    setEditNotes(config?.notes || '');
    setIsExpanded(true);
    setIsEditing(true);
  }, [label, type, config]);

  const saveEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const objectives = editObjectives
      .split('\n')
      .map((s: string) => s.trim())
      .filter(Boolean);
    update({
      label: editLabel.trim() || label,
      type:  editType,
      config: {
        ...config,
        mission:          editMission.trim(),
        objectives,
        success_criteria: editSuccessCriteria.trim(),
        notes:            editNotes.trim(),
      },
    });
    setIsEditing(false);
  }, [editLabel, editType, editMission, editObjectives, editSuccessCriteria, editNotes, config, label, update]);

  const cancelEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
  }, []);

  const formatTime = (t?: string) => t ? new Date(t).toLocaleTimeString() : '';

  // ── Shared input classes ──────────────────────────────────────────────────────

  const inputCls = `w-full px-2 py-1 text-xs rounded border focus:outline-none focus:ring-1 focus:ring-blue-400 ${
    darkMode
      ? 'bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;

  const labelCls = `block text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${
    darkMode ? 'text-gray-400' : 'text-gray-500'
  }`;

  const cardBg     = darkMode ? meta.cardDarkBg     : meta.cardBg;
  const cardBorder = darkMode ? meta.cardDarkBorder  : meta.cardBorder;

  // ── Container classes ─────────────────────────────────────────────────────────

  const containerCls = [
    'relative rounded-xl shadow-md transition-all duration-200',
    'min-w-[260px] max-w-[320px]',
    darkMode ? 'text-gray-100' : 'text-gray-900',
    cardBg,
    selected
      ? `ring-2 ring-blue-400 shadow-blue-400/30 shadow-lg border ${cardBorder}`
      : isValidConnectionTarget
        ? 'ring-2 ring-green-400 shadow-green-400/40 shadow-lg animate-pulse border border-green-400'
        : isConnectionSource
          ? `ring-2 ring-blue-300 shadow-blue-300/30 shadow-lg border ${cardBorder}`
          : `border ${cardBorder}`,
    onCriticalPath ? 'border-l-4 border-l-red-500' : '',
    canEdit ? 'cursor-default' : 'cursor-pointer hover:-translate-y-0.5',
  ].filter(Boolean).join(' ');

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div
      className={containerCls}
      onClick={!canEdit ? toggleExpand : undefined}
    >
      {/* ── Handles ──────────────────────────────────────────────────────────── */}

      {/* Top row: Input flow (left) + Artifacts In (right) */}
      <div className="absolute -top-5 left-0 right-0 flex justify-between px-6 pointer-events-none">
        <div className="flex flex-col items-center pointer-events-auto">
          <span className="text-[9px] font-semibold uppercase tracking-wide opacity-50 mb-0.5">In</span>
          <Handle type="target" position={Position.Top} id="input"
            isConnectable={isConnectable && canEdit}
            style={{ position: 'relative', top: 0, left: 0, transform: 'none',
                     background: '#6b7280', width: 9, height: 9, border: '2px solid white' }} />
        </div>
        <div className="flex flex-col items-center pointer-events-auto">
          <span className="text-[9px] font-semibold uppercase tracking-wide opacity-50 mb-0.5">Artifacts In</span>
          <Handle type="target" position={Position.Top} id="artifacts_in"
            isConnectable={isConnectable && canEdit}
            style={{ position: 'relative', top: 0, left: 0, transform: 'none',
                     background: '#9333ea', width: 9, height: 9, border: '2px solid white' }} />
        </div>
      </div>

      {/* Left: Tools */}
      <div className="absolute -left-10 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none">
        <span className="text-[9px] font-semibold uppercase tracking-wide opacity-50">Tools</span>
        <Handle type="target" position={Position.Left} id="tools"
          isConnectable={isConnectable && canEdit}
          style={{ position: 'relative', top: 0, left: 0, transform: 'none',
                   background: '#10b981', width: 9, height: 9, border: '2px solid white',
                   pointerEvents: 'auto' }} />
      </div>

      {/* Right: Reviewer (upper) + Personas (lower) */}
      <div className="absolute -right-14 top-1/3 -translate-y-1/2 flex items-center gap-1 pointer-events-none">
        <Handle type="target" position={Position.Right} id="reviewer"
          isConnectable={isConnectable && canEdit}
          style={{ position: 'relative', top: 0, left: 0, transform: 'none',
                   background: '#9333ea', width: 9, height: 9, border: '2px solid white',
                   pointerEvents: 'auto' }} />
        <span className="text-[9px] font-semibold uppercase tracking-wide opacity-50">Reviewer</span>
      </div>
      <div className="absolute -right-14 top-2/3 -translate-y-1/2 flex items-center gap-1 pointer-events-none">
        <Handle type="target" position={Position.Right} id="personas"
          isConnectable={isConnectable && canEdit}
          style={{ position: 'relative', top: 0, left: 0, transform: 'none',
                   background: '#3b82f6', width: 9, height: 9, border: '2px solid white',
                   pointerEvents: 'auto' }} />
        <span className="text-[9px] font-semibold uppercase tracking-wide opacity-50">Personas</span>
      </div>

      {/* Bottom row: Output flow (left) + Artifacts Out (right) */}
      <div className="absolute -bottom-5 left-0 right-0 flex justify-between px-6 pointer-events-none">
        <div className="flex flex-col items-center pointer-events-auto">
          <Handle type="source" position={Position.Bottom} id="output"
            isConnectable={isConnectable && canEdit}
            style={{ position: 'relative', top: 0, left: 0, transform: 'none',
                     background: '#6b7280', width: 9, height: 9, border: '2px solid white' }} />
          <span className="text-[9px] font-semibold uppercase tracking-wide opacity-50 mt-0.5">Out</span>
        </div>
        <div className="flex flex-col items-center pointer-events-auto">
          <Handle type="source" position={Position.Bottom} id="artifacts_out"
            isConnectable={isConnectable && canEdit}
            style={{ position: 'relative', top: 0, left: 0, transform: 'none',
                     background: '#9333ea', width: 9, height: 9, border: '2px solid white' }} />
          <span className="text-[9px] font-semibold uppercase tracking-wide opacity-50 mt-0.5">Artifacts Out</span>
        </div>
      </div>

      {/* ── Card header ──────────────────────────────────────────────────────── */}

      <div className={`px-3 pt-4 pb-2 rounded-t-xl border-b ${cardBorder}`}>
        {/* Phase type badge + status */}
        <div className="flex items-center justify-between mb-1.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
            darkMode ? meta.darkColor : meta.color
          }`}>
            {meta.icon} {meta.label}
          </span>
          <div className="flex items-center gap-1.5">
            {onCriticalPath && (
              <span className="text-[9px] font-bold text-red-500 uppercase tracking-wide">🔴 Critical</span>
            )}
            <span className={`flex items-center gap-1 text-[10px] font-medium ${statusM.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusM.dot}`} />
              {statusM.label}
            </span>
          </div>
        </div>

        {/* Label row */}
        {isEditing ? (
          <input
            className={inputCls}
            value={editLabel}
            onChange={e => setEditLabel(e.target.value)}
            onClick={e => e.stopPropagation()}
            placeholder="Phase name…"
          />
        ) : (
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-bold text-sm leading-tight truncate flex-1">
              {label || 'Unnamed Phase'}
            </h3>
            {canEdit && (
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={openEdit}
                  className={`w-5 h-5 flex items-center justify-center rounded text-[11px] transition-colors ${
                    darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                  }`}
                  title="Edit phase"
                >✎</button>
                <button
                  onClick={toggleExpand}
                  className={`w-5 h-5 flex items-center justify-center rounded text-[11px] transition-colors ${
                    darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                  }`}
                  title={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? '▲' : '▼'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Always-visible summary strip ─────────────────────────────────────── */}

      <div className="px-3 py-2 space-y-1.5">
        {config?.mission && (
          <p className={`text-[11px] italic leading-snug line-clamp-2 ${
            darkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {config.mission}
          </p>
        )}

        {/* Objective count badge — only when collapsed */}
        {!isExpanded && (config?.objectives || []).length > 0 && (
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
            darkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-50 text-amber-700'
          }`}>
            🎯 {config.objectives.length} objective{config.objectives.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* ── Expanded / edit panel ─────────────────────────────────────────────── */}

      {(isExpanded || isEditing) && (
        <div className={`px-3 pb-3 border-t text-xs space-y-3 ${cardBorder}`}>

          {isEditing ? (
            /* ── EDIT MODE ──────────────────────────────────────────────────── */
            <div className="space-y-2 pt-2" onClick={e => e.stopPropagation()}>

              <div>
                <label className={labelCls}>Phase Type</label>
                <select value={editType} onChange={e => setEditType(e.target.value)} className={inputCls}>
                  {Object.entries(PHASE_META).map(([k, v]) => (
                    <option key={k} value={k}>{v.icon} {v.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Mission Statement</label>
                <textarea
                  value={editMission}
                  onChange={e => setEditMission(e.target.value)}
                  className={inputCls}
                  rows={2}
                  placeholder="One sentence: what is this phase trying to achieve?"
                />
              </div>

              <div>
                <label className={labelCls}>Objectives (one per line)</label>
                <textarea
                  value={editObjectives}
                  onChange={e => setEditObjectives(e.target.value)}
                  className={inputCls}
                  rows={3}
                  placeholder={`Identify key stakeholders\nDraft initial proposal\nValidate assumptions`}
                />
              </div>

              <div>
                <label className={labelCls}>Success Criteria</label>
                <textarea
                  value={editSuccessCriteria}
                  onChange={e => setEditSuccessCriteria(e.target.value)}
                  className={inputCls}
                  rows={2}
                  placeholder="How do we know this phase is done?"
                />
              </div>

              <div>
                <label className={labelCls}>Notes / Context</label>
                <textarea
                  value={editNotes}
                  onChange={e => setEditNotes(e.target.value)}
                  className={inputCls}
                  rows={2}
                  placeholder="Constraints, references, or additional context…"
                />
              </div>

              <div className="flex items-center gap-2 opacity-40 cursor-not-allowed select-none" title="Coming soon">
                <input
                  type="checkbox"
                  id={`review-${data.id}`}
                  checked={false}
                  onChange={() => {}}
                  disabled
                  className="rounded cursor-not-allowed"
                />
                <label htmlFor={`review-${data.id}`} className={labelCls + ' mb-0 cursor-not-allowed'}>
                  Enable review step
                  <span className={`ml-1.5 text-[9px] font-normal normal-case tracking-normal ${
                    darkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    (coming soon)
                  </span>
                </label>
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={saveEdit}
                  className="flex-1 py-1 rounded text-[11px] font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                >
                  ✓ Save
                </button>
                <button
                  onClick={cancelEdit}
                  className={`flex-1 py-1 rounded text-[11px] font-semibold transition-colors ${
                    darkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  ✕ Cancel
                </button>
              </div>
            </div>

          ) : (
            /* ── READ-ONLY EXPANDED ─────────────────────────────────────────── */
            <div className="space-y-2 pt-2">

              {config?.mission && (
                <div>
                  <div className={labelCls}>Mission</div>
                  <p className={`leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {config.mission}
                  </p>
                </div>
              )}

              {(config?.objectives || []).length > 0 && (
                <div>
                  <div className={labelCls}>Objectives</div>
                  <ul className={`space-y-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {config.objectives.map((obj: string, i: number) => (
                      <li key={i} className="flex items-start gap-1">
                        <span className="text-amber-500 mt-0.5 shrink-0">•</span>
                        <span>{obj}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {config?.success_criteria && (
                <div>
                  <div className={labelCls}>Success Criteria</div>
                  <p className={`leading-snug ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {config.success_criteria}
                  </p>
                </div>
              )}

              {(config?.personas || []).length > 0 && (
                <div>
                  <div className={labelCls}>Personas</div>
                  <div className="flex flex-wrap gap-1">
                    {config.personas.map((p: any, i: number) => {
                      const displayName =
                        p.persona_data?.personaName ||
                        p.persona_name ||
                        p.personaName ||
                        p.persona_id?.replace(/-/g, ' ');
                      return (
                        <span key={i} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                          darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700'
                        }`}>
                          👤 {displayName}
                          {p.role && <span className="opacity-60">· {p.role}</span>}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {(config?.tools || []).length > 0 && (
                <div>
                  <div className={labelCls}>Tools</div>
                  <div className="flex flex-wrap gap-1">
                    {config.tools.map((t: string, i: number) => (
                      <span key={i} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                        darkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-50 text-green-700'
                      }`}>
                        🔧 {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {config?.notes && (
                <div>
                  <div className={labelCls}>Notes</div>
                  <p className={`leading-snug italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {config.notes}
                  </p>
                </div>
              )}

              {execution && (execution.start_time || execution.end_time) && (
                <div>
                  <div className={labelCls}>Execution</div>
                  <div className={`space-y-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {execution.start_time && <div>▶ {formatTime(execution.start_time)}</div>}
                    {execution.end_time   && <div>■ {formatTime(execution.end_time)}</div>}
                    {execution.artifacts?.length > 0 && (
                      <div>📎 {execution.artifacts.length} artifact{execution.artifacts.length !== 1 ? 's' : ''}</div>
                    )}
                  </div>
                </div>
              )}

              {config?.enable_review && (
                <div>
                  <div className={labelCls}>Review</div>
                  <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                    darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-50 text-purple-700'
                  }`}>
                    🔎 {config.review_behavior === 'designated' ? 'Designated Reviewer'
                       : config.review_behavior === 'chair_role' ? 'Chair Role'
                       : config.review_behavior === 'specific'   ? (config.reviewer_persona_id || 'Specific')
                       : 'Enabled'}
                  </span>
                </div>
              )}

              {canEdit && (
                <button
                  onClick={toggleExpand}
                  className={`w-full text-center text-[10px] py-0.5 rounded transition-colors ${
                    darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  ▲ Collapse
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Expand prompt when collapsed in view mode */}
      {!isExpanded && !isEditing && !canEdit && (
        <div className="px-3 pb-2 text-center">
          <button
            onClick={toggleExpand}
            className={`text-[10px] transition-colors ${
              darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            ▼ Show details
          </button>
        </div>
      )}
    </div>
  );
};

export default UnifiedPhaseNode;
